import { useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import {
	Button,
	getLastSyncDisplay,
	getSourceIcon,
	getStatusBadge,
	SpinnerWithBackground,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@viapeople/components'
import { useState } from 'react'
import { trpc } from '../../utils/trpc'
import { TriggerHistorySection } from './TriggerHistorySection'
import { TriggerWorkflowDialog } from './TriggerWorkflowDialog'

type RouterOutput = inferRouterOutputs<AppRouter>
type CrmIntegration = RouterOutput['client']['dealTeamWorkflows']['listIntegrations'][number]

interface WorkflowsTabProps {
	organizationId: string
}

export function WorkflowsTab({ organizationId }: WorkflowsTabProps) {
	const [triggerTarget, setTriggerTarget] = useState<CrmIntegration | null>(null)

	const {
		data: integrations,
		isLoading,
		error,
	} = useQuery({
		...trpc.client.dealTeamWorkflows.listIntegrations.queryOptions({ organizationId }),
		enabled: !!organizationId,
	})

	const integrationList = integrations || []

	return (
		<div className='flex flex-col gap-4'>
			<Text variant='medium' weight='semibold'>
				CRM Integrations
			</Text>

			{isLoading ? (
				<div className='py-12'>
					<SpinnerWithBackground backgroundClass='opacity-100' />
				</div>
			) : error ? (
				<div className='py-6 px-6 border border-red-200 bg-red-50 rounded-lg'>
					<Text variant='small' weight='semibold' className='text-red-700 mb-1'>
						Failed to load workflows
					</Text>
					<Text variant='xsmall' className='text-red-600'>
						{error.message}
					</Text>
				</div>
			) : integrationList.length === 0 ? (
				<div className='py-12 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 rounded-lg'>
					<Text variant='medium' weight='semibold' className='text-neutral-600 mb-2'>
						No Workflows Configured
					</Text>
					<Text variant='small' className='text-neutral-500 text-center max-w-md'>
						Workflows are configured by your administrator. Once configured, you'll be able to trigger them here.
					</Text>
				</div>
			) : (
				<div className='border border-neutral-200 rounded-lg overflow-hidden bg-white shadow-sm'>
					<div className='grid grid-cols-[1fr_100px_120px_90px_120px] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200'>
						<Text
							variant='xsmall'
							weight='semibold'
							className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
							Name
						</Text>
						<Text
							variant='xsmall'
							weight='semibold'
							className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
							Source
						</Text>
						<Text
							variant='xsmall'
							weight='semibold'
							className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
							Last Sync
						</Text>
						<Text
							variant='xsmall'
							weight='semibold'
							className='text-neutral-500 uppercase tracking-wider whitespace-nowrap'>
							Status
						</Text>
						<Text
							variant='xsmall'
							weight='semibold'
							className='text-neutral-500 uppercase tracking-wider whitespace-nowrap text-right'>
							Actions
						</Text>
					</div>

					{integrationList.map((integration) => (
						<div
							key={integration.id}
							className='grid grid-cols-[1fr_100px_120px_90px_120px] gap-4 px-6 py-4 items-center border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors'>
							<Tooltip>
								<TooltipTrigger asChild>
									<div className='min-w-0'>
										<Text variant='small' weight='semibold' className='truncate block text-neutral-900'>
											{integration.name}
										</Text>
									</div>
								</TooltipTrigger>
								<TooltipContent>{integration.name}</TooltipContent>
							</Tooltip>

							{getSourceIcon(integration.provider)}
							{getLastSyncDisplay(integration)}
							<div>{getStatusBadge(integration.status)}</div>

							<div className='flex items-center justify-end'>
								{integration.status === 'active' && (
									<Button variant='secondary' size='small' onClick={() => setTriggerTarget(integration)}>
										Trigger
									</Button>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			<TriggerHistorySection organizationId={organizationId} />

			{triggerTarget && (
				<TriggerWorkflowDialog
					isOpen={!!triggerTarget}
					onClose={() => setTriggerTarget(null)}
					organizationId={organizationId}
					integration={triggerTarget}
				/>
			)}
		</div>
	)
}
